import React, { useState, useContext } from "react";
import Page from "components/layouts/Page";
import {
  BatchDefaultsRentControlTypesFormClient,
  BatchDefaultsRentControlTypesForm,
  RentControlTypesForm,
  RentControlTypesFormRentControlType,
  RentControlTypesFormLimit,
  ApiException
} from "api";
import RequirePermission from "../RequirePermission";
import {
  useRemoteData,
  responseHandlers,
  makeRemoteRequest
} from "components/useRemoteData";
import { useApiEndpoint, getApiEndpoint } from "components/useApiEndpoint";
import { RouteComponentProps } from "react-router";
import { IdentityContext } from "components/IdentityProvider";
import BatchDefaultsBatchInformationArea from "components/forms/BatchDefaultsBatchInformationArea";
import RentControlTypesFormTable from "components/forms/RentControlTypesFormTable";
import RentControlTypeLimitsFormTable from "components/forms/RentControlTypeLimitsFormTable";
import Spinner from "components/controls/Spinner";
import useMessageArea from "components/useMessageArea";
import Spacer from "components/controls/Spacer";
import Section from "components/controls/Section";
import Heading from "components/controls/Heading";
import ContentArea from "components/layouts/ContentArea";
import { BatchDefaultsEditMode } from "components/routes";
import listUtils from "utils/listUtils";
import newRowUtils from "utils/newRowUtils";
import utils from "utils";
import BatchRegulationsMenu from "../BatchRegulationsMenu";

interface MatchParams {
  id: string;
  mode: BatchDefaultsEditMode;
}

interface PageProps extends RouteComponentProps<MatchParams> {}

interface PageState {
  form: BatchDefaultsRentControlTypesForm | undefined;
  hasFormChanged: boolean;
}

interface LoadingState {
  isLoading: boolean;
}

const BatchDefaultsRentControlTypesPage: React.FC<PageProps> = function (
  props: PageProps
) {
  const batchKey: string = props.match.params.id;
  const batchDefaultsEditMode: BatchDefaultsEditMode = props.match.params.mode;

  const [page, setPage] = useState<PageState>({
    form: undefined,
    hasFormChanged: false
  });

  const [
    messageAreaState,
    setMessageBasedOnForm,
    setMessageBasedOnApiException
  ] = useMessageArea();

  const onUpdate = function (
    form: BatchDefaultsRentControlTypesForm | undefined | null,
    hasFormChanged: boolean = true
  ): void {
    setPage({
      ...page,
      form: {
        ...form,
        defaults: {
          ...(form && form.defaults)
        }
      },
      hasFormChanged: hasFormChanged
    });
  };

  const setDefaults = function (defaults: RentControlTypesForm) {
    setPage({
      ...page,
      form: {
        ...page.form,
        defaults: defaults
      },
      hasFormChanged: true
    });
  };

  const [dataState] = useRemoteData(
    useApiEndpoint(
      BatchDefaultsRentControlTypesFormClient,
      "getBatchDefaultsRentControlTypesForm"
    ),
    [batchKey],
    {
      autoFetch: true,
      onChange: (form) => {
        onUpdate(form, false);
      }
    }
  );

  const handleSubmitError = function (error: ApiException) {
    responseHandlers.use({
      "400": (ex: ApiException) => {
        const form = ex.result;
        setMessageBasedOnForm(form);
        setPage({ ...page, form: form });
      },
      _: (ex) => {
        setMessageBasedOnApiException(ex);
      }
    })(error);
  };

  const submitEndpoint = "saveBatchDefaultsRentControlTypesForm";
  const [submitState] = useRemoteData(
    useApiEndpoint(BatchDefaultsRentControlTypesFormClient, submitEndpoint),
    [batchKey, page.form || {}],
    {
      onChange: (form) => {
        setMessageBasedOnForm(form);
        onUpdate(form, false);
      },
      onError: handleSubmitError
    }
  );

  const [loadingState, setLoadingState] = useState<LoadingState>({
    isLoading: false
  });
  const identity = useContext(IdentityContext);
  const saveFormPromise = (): Promise<any> => {
    if (!page.hasFormChanged) {
      return utils.getEmptyPromise();
    }

    setLoadingState({ isLoading: true });

    return makeRemoteRequest(
      getApiEndpoint(
        identity,
        BatchDefaultsRentControlTypesFormClient,
        submitEndpoint
      ),
      [batchKey, page.form || {}]
    )
      .catch((error) => {
        handleSubmitError(error);
        return utils.getRejectedPromise();
      })
      .finally(() => setLoadingState({ isLoading: false }));
  };

  const batch = page.form && page.form.batchDetail;
  const spinner = (
    <Spinner
      isVisible={
        dataState.isFetching || submitState.isFetching || loadingState.isLoading
      }
    />
  );

  if (page.form == null && (dataState.isFetching || submitState.isFetching)) {
    return spinner;
  } else if (page.form == null) {
    return messageAreaState.messageArea;
  }

  if (!page.form.defaults) {
    return <span>Error with current defaults.</span>;
  }

  if (!batch) {
    return <span>Error with batch information.</span>;
  }

  const defaults = page.form.defaults;
  const isEditEnabled = !!(
    defaults.pageConfiguration && defaults.pageConfiguration.isEditEnabled
  );

  const onRentControlTypesChange = (
    rentControlTypes: RentControlTypesFormRentControlType[]
  ) => {
    setDefaults({
      ...defaults,
      rentControlTypes: rentControlTypes
    });
  };

  const addRentControlTypeRow = function (): void {
    onRentControlTypesChange(
      (defaults.rentControlTypes || []).concat({
        key: newRowUtils.nextTemporaryKey(),
        isChanged: true
      })
    );
  };

  /*
   * Only a rent control that no configuration in this batch selects can be removed, so its allowed increases are
   * removed with it rather than being left pointing at a rent control that is gone.
   */
  const removeRentControlTypeRow = function (
    rowToRemove: RentControlTypesFormRentControlType
  ): void {
    setDefaults({
      ...defaults,
      rentControlTypes: listUtils.copyListWithItemRemoved(
        defaults.rentControlTypes || [],
        rowToRemove
      ),
      limits: (defaults.limits || []).filter(
        (limit) =>
          limit.selectedRentControlType?.key !== rowToRemove.key
      )
    });
  };

  const onLimitsChange = (
    limits: RentControlTypesFormLimit[]
  ) => {
    setDefaults({
      ...defaults,
      limits: limits
    });
  };

  const addIncreaseLimitRow = function (): void {
    onLimitsChange(
      (defaults.limits || []).concat({
        key: newRowUtils.nextTemporaryKey(),
        isChanged: true
      })
    );
  };

  const removeIncreaseLimitRow = function (
    rowToRemove: RentControlTypesFormLimit
  ): void {
    onLimitsChange(
      listUtils.copyListWithItemRemoved(
        defaults.limits || [],
        rowToRemove
      )
    );
  };

  const menu = (
    <BatchRegulationsMenu
      onClick={saveFormPromise}
      batchKey={batchKey}
      title={"Rent Control Types"}
      batchDefaultsEditMode={batchDefaultsEditMode}
      hasUnsavedChanges={page.hasFormChanged}
      pageConfiguration={defaults.pageConfiguration}
    />
  );

  return (
    <Page menu={menu}>
      <RequirePermission
        permissions={["canViewBatchDefaultsForms"]}
        accessDenied="page"
      >
        {spinner}
        {messageAreaState.messageArea}
        <BatchDefaultsBatchInformationArea batchInformation={{ ...batch }} />
        <Section title="RENT CONTROL TYPES" />
        <Spacer size="l" orientation="v" />

        <ContentArea columnSize={"12"}>
          <Heading level="3">Rent controls</Heading>
          <Spacer size="s" orientation="v" />
          <RentControlTypesFormTable
            rentControlTypes={defaults.rentControlTypes}
            onChange={onRentControlTypesChange}
            onAddRowAction={isEditEnabled ? addRentControlTypeRow : undefined}
            onRemoveRowAction={removeRentControlTypeRow}
            canRemoveRow={(row) =>
              newRowUtils.isUnsaved(row.key) || !row.isUsedByRentControlDefaults
            }
            pageConfiguration={defaults.pageConfiguration}
          />
        </ContentArea>

        <Spacer size="l" orientation="v" />

        <ContentArea columnSize={"12"}>
          <Heading level="3">Allowed increases for this batch</Heading>
          <Spacer size="s" orientation="v" />
          <RentControlTypeLimitsFormTable
            limits={defaults.limits}
            rentControlTypes={defaults.rentControlTypes || []}
            onChange={onLimitsChange}
            onAddRowAction={isEditEnabled ? addIncreaseLimitRow : undefined}
            onRemoveRowAction={removeIncreaseLimitRow}
            pageConfiguration={defaults.pageConfiguration}
          />
        </ContentArea>
      </RequirePermission>
    </Page>
  );
};

export default BatchDefaultsRentControlTypesPage;
